import React, { useCallback, useEffect, useMemo } from "react";
import CustomStepper from "../../../../components/common/CustomStepper";
import { Wrapper, Title, Description } from "./styles";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { setStep } from "../../redux/formDataSlice";
import { CountryConfig, StepConfig } from "./types";
import Amount from "./steps/Amount";
import Sender from "./steps/Sender";
import Beneficiary from "./steps/Beneficiary";
import PaymentDetails from "./steps/PaymentDetails";
import Review from "./steps/Review";

interface OrchestratorProps {
  config: CountryConfig;
}

const AsiaPayoutOrchestrator: React.FC<OrchestratorProps> = ({ config }) => {
  const dispatch = useAppDispatch();
  const step = useAppSelector((state) => state.formData.step);
  const countryCode = (config.dataSliceKey || config.code).toLowerCase();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData);
  const getSavedData = asiaPayoutData?.[countryCode] || {};

  useEffect(() => {
    dispatch(setStep(0));
  }, [dispatch, countryCode]);

  const filteredSteps: StepConfig[] = useMemo(() => {
    return config.steps.filter((s) => {
      if (s.hideIf) {
        return !s.hideIf(getSavedData);
      }
      return true;
    });
  }, [config, getSavedData]);

  const stepsItems = filteredSteps.map((s, index) => ({
    id: `step-${index}`,
    title: s.title,
    description: s.description,
    stepTitle: s.stepTitle,
    stage: index,
  }));

  const handleChangeStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < filteredSteps.length) {
        dispatch(setStep(index));
      }
    },
    [dispatch, filteredSteps.length]
  );

  const renderStepContent = useCallback(() => {
    const currentStepData = filteredSteps[step];
    if (!currentStepData) return null;

    const handleNext = () => handleChangeStep(step + 1);
    const handlePrevious = () => handleChangeStep(step - 1);

    switch (currentStepData.type) {
      case "amount":
        return <Amount config={config} handleNext={handleNext} handlePrevious={handlePrevious} />;
      case "sender":
        return <Sender config={config} handleNext={handleNext} handlePrevious={handlePrevious} />;
      case "beneficiary":
        return <Beneficiary config={config} handleNext={handleNext} handlePrevious={handlePrevious} />;
      case "payment_details":
        return <PaymentDetails config={config} handleNext={handleNext} handlePrevious={handlePrevious} />;
      case "review":
        return <Review config={config} handleNext={handleNext} handlePrevious={handlePrevious} />;
      default:
        if (currentStepData.component) {
          const StepComponent = currentStepData.component;
          return <StepComponent handleNext={handleNext} handlePrevious={handlePrevious} />;
        }
        return null;
    }
  }, [step, filteredSteps, config, handleChangeStep]);

  return (
    <Wrapper>
      <CustomStepper stages={stepsItems} step={step} setStep={setStep} />
      <Title>{stepsItems[step]?.stepTitle}</Title>
      <Description>{stepsItems[step]?.description}</Description>
      {renderStepContent()}
    </Wrapper>
  );
};

export default AsiaPayoutOrchestrator;
